import { io } from 'socket.io-client';

let socket = null;
let activeSignature = '';
let activeRooms = [];
let activeUser = null;
let debugBound = false;

const CLIENT_ID_KEY = 'gt_ws_client_id';


function createClientId() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `gt_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function getClientId() {
  try {
    let clientId = window.sessionStorage.getItem(CLIENT_ID_KEY);

    if (!clientId) {
      clientId = createClientId();
      window.sessionStorage.setItem(CLIENT_ID_KEY, clientId);
    }

    return clientId;
  } catch (_error) {
    return createClientId();
  }
}

function normalizeUrl(value) {
  const raw = String(value || '').trim();

  if (!raw || raw === 'undefined' || raw === 'null') {
    return '';
  }

  return raw.replace(/\/$/, '');
}

function getWebSocketUrl() {
  const envUrl = normalizeUrl(process.env.VUE_APP_WEBSOCKET_URL);
  if (envUrl) return envUrl;

  const host = window.location.hostname;

  if (host === 'localhost' || host === '127.0.0.1') {
    return 'http://localhost:3011';
  }

  return 'https://websockets.gestiontotal.app';
}

function getRooms(context) {
  const rooms = [];

  if (context?.company?.token) {
    rooms.push(`company_${context.company.token}`);
  }

  if (context?.branch?.token) {
    rooms.push(`branch_${context.branch.token}`);
  }

  return [...new Set(rooms)];
}

function buildSignature(context, user) {
  return `${context?.company?.token || ''}:${context?.branch?.token || ''}:${user?.token || ''}:${getClientId()}`;
}

function joinRooms() {
  if (!socket || !socket.connected || !activeUser) return;

  activeRooms.forEach((room) => {
    socket.emit('joinRoom', {
      room,
      username: activeUser?.name || activeUser?.email || 'Usuario',
      token: activeUser?.token || '',
      client_id: getClientId(),
    });
  });
}

function bindDebugEvents() {
  if (!socket || debugBound) return;
  debugBound = true;

  socket.on('connect', () => {
    // eslint-disable-next-line no-console
    console.info('[GT WS] conectado', {
      id: socket.id,
      url: getWebSocketUrl(),
      transport: socket.io.engine.transport.name,
      rooms: activeRooms,
    });

    joinRooms();
  });

  socket.io.engine.on('upgrade', (transport) => {
    // eslint-disable-next-line no-console
    console.info('[GT WS] transporte actualizado', transport.name);
  });

  socket.on('connect_error', (error) => {
    // eslint-disable-next-line no-console
    console.warn('[GT WS] connect_error', {
      message: error?.message,
      description: error?.description,
      context: error?.context,
      url: getWebSocketUrl(),
    });
  });

  socket.on('disconnect', (reason) => {
    // eslint-disable-next-line no-console
    console.warn('[GT WS] desconectado', reason);
  });

  socket.io.on('reconnect_attempt', (attempt) => {
    // eslint-disable-next-line no-console
    console.info('[GT WS] reintento', attempt);
  });

  socket.io.on('reconnect', () => {
    joinRooms();
  });
}

export function connectWebSocket(context, user) {
  if (!context?.company?.token || !user?.token) return null;

  const signature = buildSignature(context, user);
  activeRooms = getRooms(context);
  activeUser = user;

  if (socket && activeSignature !== signature) {
    disconnectWebSocket();
  }

  if (!socket) {
    activeSignature = signature;
    debugBound = false;

    socket = io(getWebSocketUrl(), {
      path: '/socket.io',
      query: {
        token: user.token,
        username: user.name || user.email || 'Usuario',
        company: context.company.token,
        branch: context.branch?.token || '',
        client_id: getClientId(),
      },

      // Mas estable tras Nginx: primero polling, luego upgrade a websocket.
      transports: ['polling', 'websocket'],
      upgrade: true,
      rememberUpgrade: false,
      withCredentials: false,

      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      randomizationFactor: 0.5,
      timeout: 20000,
      autoConnect: true,
    });

    bindDebugEvents();
  } else if (socket.connected) {
    joinRooms();
  } else if (!socket.active) {
    socket.connect();
  }

  return socket;
}

export function disconnectWebSocket() {
  if (socket) {
    socket.removeAllListeners();
    socket.io.removeAllListeners();
    socket.disconnect();
  }

  socket = null;
  activeSignature = '';
  activeRooms = [];
  activeUser = null;
  debugBound = false;
}

export function getSocket() {
  return socket;
}

export function getOrConnectSocket(context, user) {
  if (socket) return socket;
  return connectWebSocket(context, user);
}

export function isSocketConnected() {
  return !!socket?.connected;
}
